import { BugOutlined, CloseOutlined, CopyOutlined, HomeOutlined, ReloadOutlined } from "@ant-design/icons";
import { App as AntApp, Button, Card, Result, Space, theme } from "../ui";
import type { ReactNode } from "react";
import { buildErrorReportText, type ClientErrorReportInput } from "./clientErrorReportText";
import type { ClientErrorRecoveryLabels } from "./clientErrorTypes";
import { typography } from "../layout/spacing";
import { DetailField, DetailList } from "../components/DetailList";

const errorStackMaxHeight = 280;
const errorRecoveryMaxWidth = 800;

type ClientErrorRecoveryViewProps = {
  children?: ReactNode;
  errorMessage?: string | null;
  labels: ClientErrorRecoveryLabels;
  mode: "page" | "overlay";
  reportInput: ClientErrorReportInput;
  showDeveloperDetails?: boolean;
  stack?: string | null;
  onBackToDashboard: () => void;
  onClose?: () => void;
  onReload?: () => void;
};

export function ClientErrorRecoveryView({
  children,
  errorMessage,
  labels,
  mode,
  reportInput,
  showDeveloperDetails,
  stack,
  onBackToDashboard,
  onClose,
  onReload = () => window.location.reload(),
}: ClientErrorRecoveryViewProps) {
  const { message } = AntApp.useApp();
  const { token } = theme.useToken();

  const copyReport = () => {
    const report = buildErrorReportText(reportInput, labels);
    if (typeof navigator.clipboard?.writeText !== "function") {
      message.error(labels.copyFailed);
      return;
    }

    void navigator.clipboard.writeText(report).then(
      () => message.success(labels.errorReportCopied),
      () => message.error(labels.copyFailed),
    );
  };

  const developerDetails = showDeveloperDetails && (stack || errorMessage || reportInput.errorMessage) ? (
    <pre style={{ margin: 0, textAlign: "left", fontSize: typography.xs, overflow: "auto", maxHeight: errorStackMaxHeight, whiteSpace: "pre-wrap", overflowWrap: "anywhere" }}>
      {stack || errorMessage || reportInput.errorMessage}
    </pre>
  ) : null;

  // Fehler-ID und Anfrage-ID sind die Suchbegriffe im Protokoll; die Fehlerstelle hilft bei der Zuordnung.
  const references = reportInput.errorId || reportInput.requestId || reportInput.errorLocation ? (
    <DetailList style={{ textAlign: "left" }}>
      {reportInput.errorId ? <DetailField label={labels.errorId}><code>{reportInput.errorId}</code></DetailField> : null}
      {reportInput.requestId ? <DetailField label={labels.requestId}><code>{reportInput.requestId}</code></DetailField> : null}
      {reportInput.errorLocation ? (
        <DetailField label={labels.errorLocation}>
          <code style={{ overflowWrap: "anywhere" }}>{reportInput.errorLocation}</code>
          {reportInput.moduleHint ? <> ({labels.moduleHint}: {reportInput.moduleHint})</> : null}
        </DetailField>
      ) : null}
    </DetailList>
  ) : null;

  const actions = (
    <Space wrap>
      <Button type="primary" icon={<HomeOutlined />} onClick={onBackToDashboard}>
        {labels.backToDashboard}
      </Button>
      <Button icon={<ReloadOutlined />} onClick={onReload}>
        {labels.reloadPage}
      </Button>
      <Button icon={<CopyOutlined />} onClick={copyReport}>
        {labels.copyErrorReport}
      </Button>
      {mode === "overlay" ? (
        onClose ? (
          <Button icon={<CloseOutlined />} onClick={onClose}>
            {labels.close}
          </Button>
        ) : null
      ) : null}
    </Space>
  );

  if (mode === "overlay") {
    return (
      <div
        data-error-overlay="react"
        style={{
          position: "fixed",
          inset: 0,
          zIndex: 100000,
          background: token.colorBgMask,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: token.paddingLG,
          boxSizing: "border-box",
        }}
      >
        <Card
          title={(
            <Space>
              <BugOutlined style={{ color: token.colorError }} />
              {labels.anErrorOccurred}
            </Space>
          )}
          style={{ maxWidth: errorRecoveryMaxWidth, width: "100%", maxHeight: "100%", overflow: "auto" }}
        >
          <Space direction="vertical" size="large" style={{ width: "100%" }}>
            {actions}
            {references}
            {developerDetails}
            {children}
          </Space>
        </Card>
      </div>
    );
  }

  return (
    <div data-error-overlay="react" style={{ padding: "calc(var(--mq-space-xl) * 2) var(--mq-space-xl)", maxWidth: errorRecoveryMaxWidth, margin: "0 auto" }}>
      <Result
        status="error"
        icon={<BugOutlined />}
        title={labels.anErrorOccurred}
        extra={(
          <Space direction="vertical" size="large" style={{ width: "100%" }}>
            {actions}
            {references}
            {developerDetails}
            {children}
          </Space>
        )}
      />
    </div>
  );
}
